import memoize from '@github/memoize'
import {hasMatch} from 'fzy.js'
import {
  type SearchProvider,
  type FilterProvider,
  FilterItem,
  type QueryEvent,
  type QueryFilterElement,
  SearchItem,
  Octicon,
} from '@github-ui/query-builder-element/query-builder-api'
import type {QueryBuilderElement} from '@github-ui/query-builder-element'

interface Suggestion {
  value: string
}

const memoizeCache = new Map()
const memoizeFetchJSON = memoize(fetchJSON, {cache: memoizeCache})

async function fetchJSON(url: string) {
  const response = await fetch(url, {headers: {Accept: 'application/json'}})

  if (response.ok) {
    return await response.json()
  } else {
    return undefined
  }
}

const getSearchUrl =
  document.querySelector<HTMLFormElement>('#notifications-search-input-form')?.getAttribute('action') || ''

const filterSuggestions = (suggestions: string[], query: string) =>
  suggestions.filter(suggestion => {
    if ((query && hasMatch(query, suggestion)) || query === '') return suggestion
  })

const searchInputId = 'query-builder-notifications-search-input'

function getSearchInput(): HTMLElement {
  return document.querySelector<HTMLElement>(`query-builder#${searchInputId}`)!
}

async function fetchAuthorSuggestions(): Promise<Suggestion[]> {
  const url = getSearchInput().getAttribute('data-suggestable-authors')!
  return memoizeFetchJSON(url)
}

async function fetchRepositoriesSuggestions(): Promise<Suggestion[]> {
  const url = getSearchInput().getAttribute('data-suggestable-repositories')!
  return memoizeFetchJSON(url)
}

async function fetchOwnersSuggestions(): Promise<Suggestion[]> {
  const url = getSearchInput().getAttribute('data-suggestable-owners')!
  return memoizeFetchJSON(url)
}

async function fetchTypesSuggestions(): Promise<Suggestion[]> {
  return JSON.parse(getSearchInput().getAttribute('data-suggestable-types')!)
}

async function fetchReasonsSuggestions(): Promise<Suggestion[]> {
  return JSON.parse(getSearchInput().getAttribute('data-suggestable-reasons')!)
}

const authors = async (): Promise<Suggestion[]> => await fetchAuthorSuggestions()
const repositories = async (): Promise<Suggestion[]> => await fetchRepositoriesSuggestions()
const types = async (): Promise<Suggestion[]> => await fetchTypesSuggestions()
const reasons = async (): Promise<Suggestion[]> => await fetchReasonsSuggestions()
const owners = async (): Promise<Suggestion[]> => await fetchOwnersSuggestions()

class SearchItemProvider extends EventTarget implements SearchProvider {
  priority = 1
  name = 'Search'
  singularItemName = 'search item'
  value = 'search'
  type = 'search' as const

  queryBuilder: QueryBuilderElement

  constructor(queryBuilder: QueryBuilderElement) {
    super()

    this.queryBuilder = queryBuilder
    this.queryBuilder.addEventListener('query', this)
    this.queryBuilder.attachProvider(this)
  }

  handleEvent(event: QueryEvent) {
    if (String(event) === '') return
    this.dispatchEvent(
      new SearchItem({
        priority: 1,
        value: event.toString(),
        icon: Octicon.Search,
        scope: 'GENERAL',
        action: {
          url: `${getSearchUrl}?query=${event.toString()}`,
        },
      }),
    )
  }
}

class AuthorProvider extends EventTarget implements FilterProvider {
  name = 'Authors'
  singularItemName = 'author'
  value = 'author'
  description = 'filter by notification author'
  priority = 2
  type = 'filter' as const

  queryBuilder: QueryBuilderElement

  constructor(queryBuilder: QueryBuilderElement) {
    super()

    this.queryBuilder = queryBuilder
    this.queryBuilder.addEventListener('query', this)
    this.queryBuilder.attachProvider(this)
  }

  async handleEvent(event: QueryEvent) {
    const lastQuery = event.parsedQuery.at(-1)
    const lastQueryValue = lastQuery?.value || ''
    const lastQueryType = lastQuery?.type
    const lastQueryFilter = (lastQuery as QueryFilterElement)?.filter || ''

    if (lastQueryType !== 'filter' && (hasMatch(lastQueryValue, this.value) || lastQueryValue === '')) {
      this.dispatchEvent(new Event('show'))
    }

    // Prevents the author filters from being fetched if the author filter was not chosen
    if (lastQueryType !== 'filter' || lastQueryFilter !== this.value) return

    const getAuthorData = await authors()
    const authorValues = getAuthorData.map(author => author.value)
    const filteredAuthors = filterSuggestions(authorValues, lastQueryValue)

    // Only show the top 5 results to avoid overwhelming the browser,
    // fixes https://github.com/github/discussions/issues/2816
    const limitedFilteredAuthors = filteredAuthors.slice(0, 5)

    for (const author of limitedFilteredAuthors) {
      this.dispatchEvent(new FilterItem({filter: 'author', value: author}))
    }
  }
}

class RepositoryProvider extends EventTarget implements FilterProvider {
  name = 'Repositories'
  singularItemName = 'repo'
  value = 'repo'
  description = 'filter by repository'
  priority = 2
  type = 'filter' as const

  queryBuilder: QueryBuilderElement

  constructor(queryBuilder: QueryBuilderElement) {
    super()

    this.queryBuilder = queryBuilder
    this.queryBuilder.addEventListener('query', this)
    this.queryBuilder.attachProvider(this)
  }

  async handleEvent(event: QueryEvent) {
    const lastQuery = event.parsedQuery.at(-1)
    const lastQueryValue = lastQuery?.value || ''
    const lastQueryType = lastQuery?.type
    const lastQueryFilter = (lastQuery as QueryFilterElement)?.filter || ''

    if (lastQueryType !== 'filter' && (hasMatch(lastQueryValue, this.value) || lastQueryValue === '')) {
      this.dispatchEvent(new Event('show'))
    }

    if (lastQueryType !== 'filter' || lastQueryFilter !== this.value) return

    const getRepoData = await repositories()
    const repoValues = getRepoData.map(repo => repo.value)
    const filteredRepos = filterSuggestions(repoValues, lastQueryValue)

    // Only show the top 5 results to avoid overwhelming the browser,
    // fixes https://github.com/github/discussions/issues/2816
    const limitedFilteredRepos = filteredRepos.slice(0, 5)

    for (const repo of limitedFilteredRepos) {
      this.dispatchEvent(new FilterItem({filter: 'repo', value: repo}))
    }
  }
}

class OwnersProvider extends EventTarget implements FilterProvider {
  name = 'Organizations'
  singularItemName = 'org'
  value = 'org'
  description = 'filter by organization'
  priority = 2
  type = 'filter' as const

  queryBuilder: QueryBuilderElement

  constructor(queryBuilder: QueryBuilderElement) {
    super()

    this.queryBuilder = queryBuilder
    this.queryBuilder.addEventListener('query', this)
    this.queryBuilder.attachProvider(this)
  }

  async handleEvent(event: QueryEvent) {
    const lastQuery = event.parsedQuery.at(-1)
    const lastQueryValue = lastQuery?.value || ''
    const lastQueryType = lastQuery?.type
    const lastQueryFilter = (lastQuery as QueryFilterElement)?.filter || ''

    if (lastQueryType !== 'filter' && (hasMatch(lastQueryValue, this.value) || lastQueryValue === '')) {
      this.dispatchEvent(new Event('show'))
    }

    if (lastQueryType !== 'filter' || lastQueryFilter !== this.value) return

    const getOwnerData = await owners()
    const ownerValues = getOwnerData.map(owner => owner.value)
    const filteredOwners = filterSuggestions(ownerValues, lastQueryValue)

    // Only show the top 5 results to avoid overwhelming the browser,
    // fixes https://github.com/github/discussions/issues/2816
    const limitedFilteredOwners = filteredOwners.slice(0, 5)

    for (const org of limitedFilteredOwners) {
      this.dispatchEvent(new FilterItem({filter: 'org', value: org}))
    }
  }
}

class TypeProvider extends EventTarget implements FilterProvider {
  name = 'Types'
  singularItemName = 'type'
  value = 'is'
  description = 'filter by status or discussion type'
  priority = 2
  type = 'filter' as const

  queryBuilder: QueryBuilderElement

  constructor(queryBuilder: QueryBuilderElement) {
    super()

    this.queryBuilder = queryBuilder
    this.queryBuilder.addEventListener('query', this)
    this.queryBuilder.attachProvider(this)
  }

  async handleEvent(event: QueryEvent) {
    const lastQuery = event.parsedQuery.at(-1)
    const lastQueryValue = lastQuery?.value || ''
    const lastQueryType = lastQuery?.type
    const lastQueryFilter = (lastQuery as QueryFilterElement)?.filter || ''

    if (lastQueryType !== 'filter' && (hasMatch(lastQueryValue, this.value) || lastQueryValue === '')) {
      this.dispatchEvent(new Event('show'))
    }

    if (lastQueryType !== 'filter' || lastQueryFilter !== this.value) return

    const getTypeData = await types()
    const typeValues = getTypeData.map(role => role.value)

    const filteredTypes = filterSuggestions(typeValues, lastQueryValue)

    for (const typeValue of filteredTypes) {
      this.dispatchEvent(new FilterItem({filter: 'is', value: typeValue}))
    }
  }
}

class ReasonProvider extends EventTarget implements FilterProvider {
  name = 'Reasons'
  singularItemName = 'reason'
  value = 'reason'
  description = 'filter by notification reason'
  priority = 2
  type = 'filter' as const

  queryBuilder: QueryBuilderElement

  constructor(queryBuilder: QueryBuilderElement) {
    super()

    this.queryBuilder = queryBuilder
    this.queryBuilder.addEventListener('query', this)
    this.queryBuilder.attachProvider(this)
  }

  async handleEvent(event: QueryEvent) {
    const lastQuery = event.parsedQuery.at(-1)
    const lastQueryValue = lastQuery?.value || ''
    const lastQueryType = lastQuery?.type
    const lastQueryFilter = (lastQuery as QueryFilterElement)?.filter || ''

    if (lastQueryType !== 'filter' && (hasMatch(lastQueryValue, this.value) || lastQueryValue === '')) {
      this.dispatchEvent(new Event('show'))
    }

    if (lastQueryType !== 'filter' || lastQueryFilter !== this.value) return

    const getTypeData = await reasons()
    const reasonValues = getTypeData.map(reason => reason.value)

    const filteredReasons = filterSuggestions(reasonValues, lastQueryValue)

    for (const reason of filteredReasons) {
      this.dispatchEvent(new FilterItem({filter: 'reason', value: reason}))
    }
  }
}

document.addEventListener('query-builder:request-provider', (event: Event) => {
  const target: QueryBuilderElement | null = event.target as QueryBuilderElement
  if (!target || target.id !== searchInputId) return

  new SearchItemProvider(event.target as QueryBuilderElement)
  new RepositoryProvider(event.target as QueryBuilderElement)
  new TypeProvider(event.target as QueryBuilderElement)
  new ReasonProvider(event.target as QueryBuilderElement)
  new AuthorProvider(event.target as QueryBuilderElement)
  new OwnersProvider(event.target as QueryBuilderElement)
})
